import { storeToRefs } from 'pinia'

import { AuthService } from '@common/auth-layer/services/auth.service'
import { useAuthStore } from '@common/auth-layer/store/auth.store'
import {
  writeAdminPreviewAgencyToken,
  writeAdminV2PreviewEnvelope,
  type AdminV2PreviewEnvelope,
} from '@common/core-layer/utils/admin-preview-token.utils'
import { writeAdminPreviewMarkerCookie } from '@common/core-layer/utils/admin-preview-cookie.utils'
import { clearAuthCookie } from '@common/core-layer/utils/cookie.utils'

import { useCoursesAdminPreviewStore } from '../stores/courses-admin-preview.ui-store'

/**
 * Single entry point for the courses admin-preview session. Use instead of the
 * store directly (mirrors the beta-preview-layer pattern).
 */
export const useCoursesAdminPreview = () => {
  const store = useCoursesAdminPreviewStore()
  const {
    isAdminPreview,
    agencyBearerToken,
    previewLocationId,
    builderMode,
    templateId,
    companyId,
    hasToken,
    adminSession,
    adminAccessToken,
  } = storeToRefs(store)

  /**
   * IAM v2 admin-preview token exchange (mirrors membership-hmi-preview `signInAsAdminV2`).
   * Exchanges the `v2.` builder token for a location-scoped IAM v2 admin session, stores it as
   * the `acatv2` envelope, and returns it on success (null on failure). No Firebase sign-in here.
   */
  async function exchangeAdminPreviewSession(
    agencyToken: string,
    locationId: string,
    companyId?: string | null
  ): Promise<AdminV2PreviewEnvelope | null> {
    try {
      // Served by the membership backend, not the clientclub base.
      const authService = new AuthService(locationId, 'MEMBERSHIP_SERVER_URL')
      // `companyId` (from the `company_id` preview URL param) lets membership-backend fall back to
      // an agency-user lookup by email + company (mirrors membership-hmi-preview PR #1329).
      const request = { agencyToken, locationId, companyId }
      const response = await authService.loginWithAdminTokenV2(request)
      const data = response.data
      const accessToken = data?.accessToken
      if (!accessToken) return null
      const envelope: AdminV2PreviewEnvelope = { ...data, token: accessToken }
      store.setAdminSession(envelope)
      // sessionStorage (client-only) so the shared BaseService reads the admin session; too large
      // for a cookie — see @common/core-layer/utils/admin-preview-token.utils.
      writeAdminV2PreviewEnvelope(envelope)
      // Per-tab sessionStorage so the raw agency token can be replayed to the federated remote on
      // reload (it runs its own sign-in exchange and can't use the exchanged admin access token).
      // Cleared when leaving /courses (see the `00.` middleware).
      writeAdminPreviewAgencyToken(agencyToken)
      // Tiny non-sensitive marker cookie (locationId + expiry, NO token) so SSR routing guards
      // recognise the preview on a full reload, since sessionStorage is server-invisible. The
      // server-minted envelope location/expiry stay authoritative.
      writeAdminPreviewMarkerCookie({ locationId: envelope.locationId ?? locationId, expiresAt: envelope.tokenExpirationTime })
      // Mutual exclusion: drop any learner session on a successful exchange so it never coexists
      // with the admin preview. Deliberately skips `useAuth().logout()` (no server `updateSession`,
      // no navigation, no query-cache teardown) — the `/courses` guards bypass learner auth during
      // a preview, so this won't bounce to `/login`. Left intact on a failed exchange.
      clearAuthCookie()
      useAuthStore().clearAuth()
      return envelope
    } catch {
      // Do not log the request error object: HTTP client errors may retain the agency token
      // in request config/body metadata.
      console.error('[courses-admin-preview] IAM v2 token exchange failed')
      return null
    }
  }

  return {
    isActive: isAdminPreview,
    agencyBearerToken,
    previewLocationId,
    builderMode,
    templateId,
    companyId,
    hasToken,
    adminSession,
    adminAccessToken,
    setAdminPreviewContext: store.setAdminPreviewContext,
    setAdminPreviewToken: store.setAdminPreviewToken,
    setAdminSession: store.setAdminSession,
    clearAdminPreview: store.clearAdminPreview,
    exchangeAdminPreviewSession,
  }
}
